import {
  getWaxUpTypeSelection,
  type MaterialCharacteristics,
} from "@jasper/client-models";
import type { MaterialAndShadeCatalog } from "@jasper/use-cases";
import type {
  DescribableItem,
  DescribableProduct,
  ProductDescriptionT,
  ProductTranslationsT,
} from "./describable.types";
import { formatTeeth } from "./teeth";

const PARTS_SEPARATOR = " · ";

// Product and item types share their values, hence one list of labels for both
const getTypeLabel = (type: string, translateProduct: ProductTranslationsT) =>
  translateProduct(type, { count: 1, defaultValue: type });

export const getProductTypeLabel = (
  product: Pick<DescribableProduct, "type">,
  translateProduct: ProductTranslationsT
) => getTypeLabel(product.type, translateProduct);

// « Bridge · 14–16 », or the type alone when the product has no teeth
export const getProductHeadline = (
  product: Pick<DescribableProduct, "type" | "teeth">,
  t: ProductDescriptionT,
  translateProduct: ProductTranslationsT
) => {
  const typeLabel = getProductTypeLabel(product, translateProduct);
  const teeth = formatTeeth(product.teeth, t);

  return teeth ? `${typeLabel}${PARTS_SEPARATOR}${teeth}` : typeLabel;
};

// « A3 · Vita Classical », or « A3 / A2 / A1 · Vita Classical » for a multi-shade (cervical / middle / incisal)
const getShadeLabel = (
  materialCharacteristics: MaterialCharacteristics,
  materialAndShadeCatalog: MaterialAndShadeCatalog,
  t: ProductDescriptionT
) => {
  const shadeCharacteristics =
    "shadeCharacteristics" in materialCharacteristics
      ? materialCharacteristics.shadeCharacteristics
      : undefined;
  if (!shadeCharacteristics) {
    return undefined;
  }

  const shadeIds = shadeCharacteristics.isMultiShade
    ? [
        shadeCharacteristics.gingivalShade,
        shadeCharacteristics.baseShade,
        shadeCharacteristics.incisalShade,
      ]
    : [shadeCharacteristics.shade];
  const shadeGuide = t(`shadeTypes.${shadeCharacteristics.shadeType}`, {
    defaultValue: "",
  });
  // The guide is written once after the shades, so each shade is its code alone.
  // Without a known guide (gingiva, unknown type), the name is all there is to show.
  const shades = shadeIds
    .map(id => materialAndShadeCatalog.shades[id])
    .filter(shade => !!shade)
    .map(({ name, colorCode }) => (shadeGuide && colorCode) || name);

  return shades.length
    ? [shades.join(" / "), shadeGuide].filter(Boolean).join(PARTS_SEPARATOR)
    : undefined;
};

// « Zircone monolithique · A3 · Vita Classical »: the material of one material characteristics, then its shade.
// Undefined when the material is missing from the catalog, so that the item is left out.
// Exported for the splinting suggestion, which describes crowns sharing one material.
export const getMaterialLabel = (
  materialCharacteristics: MaterialCharacteristics,
  materialAndShadeCatalog: MaterialAndShadeCatalog,
  t: ProductDescriptionT
) => {
  const materialName =
    materialAndShadeCatalog.materials[materialCharacteristics.material]?.name;
  if (!materialName) {
    return undefined;
  }

  return [
    materialName,
    getShadeLabel(materialCharacteristics, materialAndShadeCatalog, t),
  ]
    .filter(Boolean)
    .join(PARTS_SEPARATOR);
};

// The items of the product grouped by material label, in teeth order (arch-scoped items, without
// tooth, first). Items without material, or whose material is missing from the catalog, are left out.
const groupItemsByMaterialLabel = (
  product: DescribableProduct,
  materialAndShadeCatalog: MaterialAndShadeCatalog,
  t: ProductDescriptionT
) => {
  const groups = new Map<string, DescribableItem[]>();

  [...product.items]
    .sort(
      (a: DescribableItem, b: DescribableItem) =>
        (a.tooth ?? 0) - (b.tooth ?? 0)
    )
    .forEach(item => {
      const label =
        item.materialCharacteristics &&
        getMaterialLabel(
          item.materialCharacteristics,
          materialAndShadeCatalog,
          t
        );
      if (label) {
        groups.set(label, [...(groups.get(label) ?? []), item]);
      }
    });

  return groups;
};

/*
  The material lines of a product, one per different material label.
  A single label is enough on its own:
    Couronne · 15 → « Zircone monolithique · A3 · Vita Classical »
  With several labels, each line also says which teeth it applies to, and its item type when
  the product mixes item types, except what the headline already says (all the product's
  teeth, the product's own type):
    Bridge · 14–16 → « 14 · CCM · A2 · Vita Classical »
                     « 15–16 · Zircone monolithique · A3 · Vita Classical »
    Inlay-core · 21 → « Métal non précieux »
                      « Couronne · E.max Stratifiée · A3 · Vita Classical »
*/
const getMaterialLines = (
  product: DescribableProduct,
  materialAndShadeCatalog: MaterialAndShadeCatalog,
  t: ProductDescriptionT,
  translateProduct: ProductTranslationsT
): string[] => {
  const groups = groupItemsByMaterialLabel(product, materialAndShadeCatalog, t);

  if (groups.size <= 1) {
    return [...groups.keys()];
  }

  const hasMixedTypes =
    new Set([...groups.values()].flat().map(({ type }) => type)).size > 1;
  const productTeeth = new Set(product.teeth);

  return [...groups].map(([materialLabel, items]) => {
    const lineTeeth = new Set(
      items.flatMap(({ tooth }) => (tooth == null ? [] : [tooth]))
    );
    const coversProductTeeth =
      lineTeeth.size === productTeeth.size &&
      [...lineTeeth].every(tooth => productTeeth.has(tooth));
    const itemType = items[0].type;

    return [
      hasMixedTypes &&
        itemType !== product.type &&
        getTypeLabel(itemType, translateProduct),
      !coversProductTeeth && formatTeeth([...lineTeeth], t),
      materialLabel,
    ]
      .filter(Boolean)
      .join(PARTS_SEPARATOR);
  });
};

// V2 splits a « numerical and physical » wax-up into one item per type: getWaxUpTypeSelection merges them back
const getWaxUpLines = (
  product: DescribableProduct,
  t: ProductDescriptionT
): string[] => {
  const selection = getWaxUpTypeSelection(product.items);

  return selection ? [t(`steps.waxUpType.${selection}`)] : [];
};

// The one description of a product, the same on every page: its headline, then its material lines and its specifics (wax-up)
export const describeProduct = (
  product: DescribableProduct,
  materialAndShadeCatalog: MaterialAndShadeCatalog,
  t: ProductDescriptionT,
  translateProduct: ProductTranslationsT
) => ({
  headline: getProductHeadline(product, t, translateProduct),
  lines: [
    ...getMaterialLines(product, materialAndShadeCatalog, t, translateProduct),
    ...getWaxUpLines(product, t),
  ],
});
